import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  HStack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import moment from 'moment';

export default function TaskModal({
  isOpen,
  onClose,
  onSave,
  task = null,
  prefilledDeal = null,
  prefilledContact = null,
  prefilledDate = null,
  crmUsers = [],
}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('custom');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [hasTime, setHasTime] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [availableLeads, setAvailableLeads] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Load available leads and contacts when modal opens and type is CRM
  useEffect(() => {
    if (isOpen && !prefilledDeal && !prefilledContact && type === 'crm') {
      loadActiveLeads();
      loadActiveContacts();
    } else if (type !== 'crm') {
      // Clear selected lead/contact if type changes away from CRM
      setSelectedLeadId('');
      setSelectedContactId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type]);

  const loadActiveLeads = async () => {
    setLoadingLeads(true);
    try {
      const dealsRef = collection(db, 'deals');
      let q;
      
      if (isCRMAdmin) {
        // Admin can see all active deals
        q = query(
          dealsRef,
          where('stage', 'not-in', ['deal-lost', 'loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-won'])
        );
      } else {
        // Regular user sees only their deals
        q = query(
          dealsRef,
          where('ownerId', '==', currentUser.uid),
          where('stage', 'not-in', ['deal-lost', 'loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-won'])
        );
      }
      
      const snapshot = await getDocs(q);
      const leads = [];
      snapshot.forEach(doc => {
        leads.push({ id: doc.id, ...doc.data() });
      });
      setAvailableLeads(leads);
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const loadActiveContacts = async () => {
    setLoadingContacts(true);
    try {
      const contactsRef = collection(db, 'contacts');
      let q;
      
      if (isCRMAdmin) {
        // Admin can see all active contacts
        q = query(
          contactsRef,
          where('isActive', '==', true),
          limit(100)
        );
      } else {
        // Regular user sees only their contacts
        q = query(
          contactsRef,
          where('ownerId', '==', currentUser.uid),
          where('isActive', '==', true),
          limit(100)
        );
      }
      
      const snapshot = await getDocs(q);
      const contacts = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        contacts.push({ 
          id: doc.id, 
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        });
      });
      setAvailableContacts(contacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Initialize form when task or prefilled data changes
  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setType(task.type || 'custom');
      setPriority(task.priority || 'medium');
      setAssignedTo(task.assignedTo || currentUser.uid);
      setDueTime(task.dueTime || '');
      setHasTime(!!task.dueTime);
      
      // Handle both deal and contact relations
      if (task.relatedTo?.type === 'deal') {
        setSelectedLeadId(task.relatedTo.id || '');
        setSelectedContactId('');
      } else if (task.relatedTo?.type === 'contact') {
        setSelectedContactId(task.relatedTo.id || '');
        setSelectedLeadId('');
      }
      
      if (task.dueDate) {
        const date = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        setDueDate(moment(date).format('YYYY-MM-DD'));
      }
    } else {
      // New task defaults
      setTitle('');
      setDescription('');
      // Default to CRM type for any task created from CRM context
      setType('crm');
      setPriority('medium');
      setAssignedTo(currentUser.uid);
      setDueTime('');
      setHasTime(false);
      setSelectedLeadId(prefilledDeal?.id || '');
      setSelectedContactId(prefilledContact?.id || '');
      
      if (prefilledDate) {
        setDueDate(moment(prefilledDate).format('YYYY-MM-DD'));
      } else {
        setDueDate(moment().format('YYYY-MM-DD'));
      }
    }
  }, [task, prefilledDeal, prefilledContact, prefilledDate, currentUser.uid, isOpen]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: 'Title required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!dueDate) {
      toast({
        title: 'Due date required',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Parse date correctly to avoid timezone issues
      // Create date at local midnight, not UTC
      const [year, month, day] = dueDate.split('-');
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0);
      
      const taskData = {
        title: title.trim(),
        description: description.trim(),
        type,
        priority,
        assignedTo: assignedTo || currentUser.uid,
        assignedBy: currentUser.uid,
        dueDate: localDate,
        dueTime: hasTime ? dueTime : null,
        status: task ? task.status : 'pending',
        isCalendarEvent: false,
        eventDuration: hasTime ? 30 : null,
      };

      // Add related deal or contact if provided or selected
      if (prefilledDeal) {
        taskData.relatedTo = {
          type: 'deal',
          id: prefilledDeal.id,
          name: prefilledDeal.dealName || 'Deal',
        };
      } else if (prefilledContact) {
        taskData.relatedTo = {
          type: 'contact',
          id: prefilledContact.id,
          name: `${prefilledContact.firstName} ${prefilledContact.lastName}`,
        };
      } else if (selectedLeadId) {
        const selectedLead = availableLeads.find(l => l.id === selectedLeadId);
        if (selectedLead) {
          taskData.relatedTo = {
            type: 'deal',
            id: selectedLead.id,
            name: selectedLead.dealName || 'Deal',
          };
        }
      } else if (selectedContactId) {
        const selectedContact = availableContacts.find(c => c.id === selectedContactId);
        if (selectedContact) {
          taskData.relatedTo = {
            type: 'contact',
            id: selectedContact.id,
            name: `${selectedContact.firstName} ${selectedContact.lastName}`,
          };
        }
      } else if (task?.relatedTo) {
        taskData.relatedTo = task.relatedTo;
      }

      const result = await onSave(task ? task.id : null, taskData);

      if (result.success) {
        toast({
          title: task ? 'Task updated' : 'Task created',
          status: 'success',
          duration: 2000,
        });
        onClose();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast({
        title: 'Error saving task',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{task ? 'Edit Task' : 'Create New Task'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Task Title</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What needs to be done?"
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add details..."
                rows={3}
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Type</FormLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  size="sm"
                  isDisabled={!!prefilledDeal}
                >
                  <option value="crm">CRM</option>
                  <option value="loan">Loan</option>
                  <option value="marketing">Marketing</option>
                  <option value="custom">Custom</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Priority</FormLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  size="sm"
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Due Date</FormLabel>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={moment().format('YYYY-MM-DD')}
              />
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel fontSize="sm" mb={0}>Add Specific Time</FormLabel>
                <Switch
                  isChecked={hasTime}
                  onChange={(e) => setHasTime(e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
              {hasTime && (
                <Input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  mt={2}
                />
              )}
            </FormControl>

            {!prefilledDeal && !prefilledContact && type === 'crm' && (
              <>
                <FormControl>
                  <FormLabel fontSize="sm">Deal (Optional)</FormLabel>
                  <Select
                    placeholder="Select a deal..."
                    value={selectedLeadId}
                    onChange={(e) => {
                      setSelectedLeadId(e.target.value);
                      if (e.target.value) setSelectedContactId(''); // Clear contact if deal selected
                    }}
                    size="sm"
                    isDisabled={loadingLeads}
                  >
                    {availableLeads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {lead.dealName} - {lead.stage}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {loadingLeads ? 'Loading deals...' : `${availableLeads.length} active deals available`}
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Contact (Optional)</FormLabel>
                  <Select
                    placeholder="Select a contact..."
                    value={selectedContactId}
                    onChange={(e) => {
                      setSelectedContactId(e.target.value);
                      if (e.target.value) setSelectedLeadId(''); // Clear deal if contact selected
                    }}
                    size="sm"
                    isDisabled={loadingContacts}
                  >
                    {availableContacts.map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.firstName} {contact.lastName} {contact.email ? `(${contact.email})` : ''}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {loadingContacts ? 'Loading contacts...' : `${availableContacts.length} contacts available`}
                  </Text>
                </FormControl>
              </>
            )}

            {crmUsers.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm">Assign To</FormLabel>
                <Select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  size="sm"
                >
                  {crmUsers.map(user => (
                    <option key={user.uid} value={user.uid}>
                      {user.name || user.Name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {prefilledDeal && (
              <Text fontSize="sm" color="gray.600" bg="blue.50" p={2} borderRadius="md">
                Related to Deal: <strong>{prefilledDeal.dealName}</strong>
              </Text>
            )}

            {prefilledContact && (
              <Text fontSize="sm" color="gray.600" bg="blue.50" p={2} borderRadius="md">
                Related to Contact: <strong>{prefilledContact.firstName} {prefilledContact.lastName}</strong>
              </Text>
            )}

            {!prefilledDeal && !prefilledContact && type === 'crm' && selectedLeadId && (
              <Text fontSize="sm" color="purple.600" bg="purple.50" p={2} borderRadius="md">
                Will link to Deal: <strong>{availableLeads.find(l => l.id === selectedLeadId)?.dealName}</strong>
              </Text>
            )}

            {!prefilledDeal && !prefilledContact && type === 'crm' && selectedContactId && (
              <Text fontSize="sm" color="purple.600" bg="purple.50" p={2} borderRadius="md">
                Will link to Contact: <strong>
                  {(() => {
                    const contact = availableContacts.find(c => c.id === selectedContactId);
                    return contact ? `${contact.firstName} ${contact.lastName}` : '';
                  })()}
                </strong>
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            {task ? 'Update Task' : 'Create Task'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

